/**
 * Minimal RFC 4180 CSV writer.
 *
 * The backend parses uploaded statements with Rust's `csv` crate at its
 * defaults, so a file we generate here has to follow the same rules: a field
 * containing a comma, a double quote or a newline is wrapped in double quotes,
 * and an embedded quote is doubled. Merchant names in a card statement are
 * comma-laden by nature ("ICA SUPERMARKET, TOCKSFORS, SWE"), so a plain
 * `join(",")` would silently shift every later column of that row.
 */

const needsQuoting = (field: string): boolean =>
  field.includes(",") || field.includes('"') || /[\r\n]/.test(field);

const escapeField = (field: string): string =>
  needsQuoting(field) ? `"${field.replace(/"/g, '""')}"` : field;

/** Render one row. Exported for tests; `toCsv` is what callers want. */
export const toCsvRow = (fields: readonly string[]): string =>
  fields.map(escapeField).join(",");

/**
 * Render `rows` (header first) as a CSV document, CRLF-terminated per the RFC
 * and ending with a trailing newline so the last record is complete.
 */
export const toCsv = (rows: readonly (readonly string[])[]): string =>
  rows.map(toCsvRow).join("\r\n") + "\r\n";
